import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import Stack from '@mui/material/Stack';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Flag from '@mui/icons-material/Flag';
import HelpOutlined from '@mui/icons-material/HelpOutlined';
import { Alert } from '@/ui';
import { EventComparison, AuditDisplaySettings } from '../types';
import { formatDate } from '../../../utils/conversionFunctions';
import { SideBySideDiff } from './SideBySideDiff';
import { NaturalLanguageView } from './NaturalLanguageView';

/** Column count of the parent table; the details row spans all of it. */
const COLUMN_COUNT = 6;

interface AuditEventRowProps {
  comparison: EventComparison;
  displaySettings: AuditDisplaySettings;
  isRootEvent?: boolean;
}

type ChipColor = 'info' | 'success' | 'warning' | 'error' | 'default';

const METHOD_CHIP: Record<string, { label: string; color: ChipColor }> = {
  GET: { label: 'FIND', color: 'info' },
  POST: { label: 'CREATE', color: 'success' },
  PUT: { label: 'UPDATE', color: 'warning' },
  PATCH: { label: 'UPDATE', color: 'warning' },
  DELETE: { label: 'DELETE', color: 'error' },
};

const statusColor = (status?: number): ChipColor => {
  if (!status) return 'default';
  if (status >= 200 && status < 300) return 'success';
  if (status >= 400 && status < 500) return 'warning';
  if (status >= 500) return 'error';
  return 'default';
};

const ellipsis = (maxWidth: number) => ({
  maxWidth,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const AuditEventRow: React.FC<AuditEventRowProps> = ({ comparison, displaySettings, isRootEvent }) => {
  const { currentEvent, changes, user } = comparison;
  const [isExpanded, setIsExpanded] = useState(false);

  // React to collapse all setting
  useEffect(() => {
    if (displaySettings.collapseAllChanges) {
      setIsExpanded(false);
    }
  }, [displaySettings.collapseAllChanges]);

  const hasChanges = changes.length > 0;
  const method = currentEvent.request?.method?.toUpperCase() || 'UNKNOWN';
  const url = currentEvent.request?.url || '';
  const status = currentEvent.response?.status;

  const renderMethodChip = () => {
    if (isRootEvent) {
      return <Chip size='small' color='info' icon={<Flag fontSize='small' />} label='ROOT' />;
    }
    const chip = METHOD_CHIP[method];
    return chip ? (
      <Chip size='small' color={chip.color} label={chip.label} />
    ) : (
      <Chip size='small' label='Unknown' />
    );
  };

  const renderDiffView = () => {
    switch (displaySettings.viewMode) {
      case 'sideBySide':
        return <SideBySideDiff changes={changes} />;
      case 'naturalLanguage':
        return <NaturalLanguageView changes={changes} userName={user} />;
      default:
        return <NaturalLanguageView changes={changes} userName={user} />;
    }
  };

  return (
    <>
      <TableRow
        hover={hasChanges}
        selected={isExpanded}
        onClick={() => hasChanges && setIsExpanded(!isExpanded)}
        sx={{ cursor: hasChanges ? 'pointer' : 'default' }}
      >
        <TableCell>{formatDate(currentEvent.timestamp)}</TableCell>
        <TableCell>{user}</TableCell>
        <TableCell>{renderMethodChip()}</TableCell>
        <TableCell sx={ellipsis(300)}>
          <Box component='span' title={url} sx={{ display: 'inline-block', ...ellipsis(300) }}>
            {url}
          </Box>
        </TableCell>
        <TableCell>{status && <Chip size='small' color={statusColor(status)} label={status} />}</TableCell>
        <TableCell>
          <Stack direction='row' spacing={0.5} sx={{ alignItems: 'center' }}>
            {hasChanges ? (
              <Chip size='small' color='info' label={`${changes.length} change${changes.length !== 1 ? 's' : ''}`} />
            ) : (
              <Chip size='small' label='no changes' />
            )}
            {hasChanges && (isExpanded ? <ExpandLess fontSize='small' /> : <ExpandMore fontSize='small' />)}
          </Stack>
        </TableCell>
      </TableRow>
      {hasChanges && (
        <TableRow>
          <TableCell colSpan={COLUMN_COUNT} sx={{ p: 0, borderBottom: isExpanded ? undefined : 'none' }}>
            <Collapse in={isExpanded} unmountOnExit>
              {isRootEvent && (
                <Alert color='info' icon={<HelpOutlined fontSize='small' />} sx={{ m: 2 }}>
                  <strong>Root Event:</strong> This represents the earliest captured state of the entity within the
                  selected date range. All changes shown above are compared against this baseline.
                  <br />
                  <br />
                  <strong>Note:</strong> This may not be the original creation of the entity — adjusting the
                  &quot;Since&quot; date filter will change which event is considered the root. To see the full
                  history, select an earlier start date.
                </Alert>
              )}
              <Box sx={{ bgcolor: 'background.paper', borderRadius: 1, m: 1, p: 1 }}>{renderDiffView()}</Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};
